/**
 * <delivery-estimate> custom element + pure delivery-window helpers.
 *
 * Renders an estimated delivery-date window in the cart, computed client-side
 * (Liquid output is CDN-cached, so a server-rendered "today" would go stale).
 *
 * Loaded globally (defer) so the element is defined regardless of cart state.
 * This matters because the cart drawer re-renders via the Section Rendering API
 * (innerHTML), and scripts injected that way do NOT execute. With the definition
 * already registered, every freshly inserted <delivery-estimate> is upgraded and
 * its connectedCallback recomputes the dates.
 *
 * The pure date logic is exposed on window.__deliveryEstimate for unit tests
 * (see tests/cart-delivery-estimate.test.js); the element registration is
 * browser-only and skipped when customElements is unavailable.
 */
(function () {
  'use strict';

  // Weekday abbreviations are intentionally hardcoded in German: this is a
  // German-first storefront and the surrounding copy (prefix, "Werktage"
  // fallback) is German too. Deriving them from Intl per visitor locale would
  // mismatch the German prefix and varies in punctuation across JS engines.
  const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

  /** Returns a new Date `n` days after `d` without mutating `d`. */
  function addDay(d, n) {
    const c = new Date(d.getTime());
    c.setDate(c.getDate() + n);
    return c;
  }

  /** True if `d` is a warehouse dispatch day (Mon-Fri). */
  function isDispatchDay(d) {
    const wd = d.getDay(); // 0=Sun .. 6=Sat
    return wd >= 1 && wd <= 5;
  }

  /** True if `d` is a carrier delivery day (never Sunday; Saturday only if `saturday`). */
  function isDeliveryDay(d, saturday) {
    const wd = d.getDay();
    if (wd === 0) return false;
    if (wd === 6) return saturday;
    return true;
  }

  /** Returns a new Date `count` delivery days after `ship`. */
  function addDeliveryDays(ship, count, saturday) {
    let d = new Date(ship.getTime());
    let added = 0;
    while (added < count) {
      d = addDay(d, 1);
      if (isDeliveryDay(d, saturday)) added += 1;
    }
    return d;
  }

  /** Formats a Date as German short weekday + d.m., e.g. "Do. 18.6.". */
  function formatDay(d) {
    return WEEKDAYS[d.getDay()] + '. ' + d.getDate() + '.' + (d.getMonth() + 1) + '.';
  }

  /**
   * Computes the delivery-window label (date range only, without the prefix).
   *
   * Pure and timezone-agnostic: the caller passes "today" and the hour already
   * resolved to the shop timezone. Dispatch is Mon-Fri (after the cutoff it
   * moves to the next day); delivery days exclude Sundays and, optionally,
   * Saturdays. A missing/invalid cutoff is treated conservatively as next-day.
   *
   * @param {Date}   today - current date in the shop timezone (time part ignored)
   * @param {number} hour  - current hour (0-23) in the shop timezone
   * @param {{minDays:number,maxDays:number,cutoff:number,saturday:boolean}} opts
   * @returns {string} e.g. "Fr. 19.6. - Sa. 20.6.", or a single "Fr. 19.6.".
   */
  function computeRange(today, hour, opts) {
    const minDays = opts.minDays;
    const maxDays = opts.maxDays;
    const cutoff = isNaN(opts.cutoff) ? 0 : opts.cutoff; // no cutoff -> dispatch next day
    const saturday = !!opts.saturday;

    let ship = new Date(today.getTime());
    if (hour >= cutoff) ship = addDay(ship, 1);          // after cutoff -> next day
    while (!isDispatchDay(ship)) ship = addDay(ship, 1); // skip weekends for dispatch

    const earliest = addDeliveryDays(ship, minDays, saturday);
    const latest = addDeliveryDays(ship, maxDays, saturday);

    return earliest.getTime() === latest.getTime()
      ? formatDay(earliest)
      : formatDay(earliest) + ' - ' + formatDay(latest);
  }

  // Public surface for unit tests (mirrors the theme's window.__* convention).
  if (typeof window !== 'undefined') {
    window.__deliveryEstimate = { computeRange, addDeliveryDays, formatDay, isDeliveryDay, isDispatchDay };
  }

  // Browser only: register the custom element.
  if (typeof window === 'undefined' || !window.customElements) return;
  if (customElements.get('delivery-estimate')) return;

  /** Current wall-clock day + hour in Europe/Berlin, independent of the visitor's timezone. */
  function nowInBerlin() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Berlin',
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', hour12: false
    }).formatToParts(new Date());
    const map = {};
    parts.forEach((p) => { map[p.type] = p.value; });
    let hour = parseInt(map.hour, 10);
    if (hour === 24) hour = 0; // some engines emit 24 for midnight
    return {
      // A Date built from Berlin's Y/M/D; only its local date parts are used afterwards.
      date: new Date(parseInt(map.year, 10), parseInt(map.month, 10) - 1, parseInt(map.day, 10)),
      hour: hour
    };
  }

  /**
   * Renders the delivery-window label from data-* attributes set by
   * snippets/cart-delivery-estimate.liquid.
   */
  class DeliveryEstimate extends HTMLElement {
    /** Recompute on every (re-)insertion into the DOM. */
    connectedCallback() {
      try {
        this.update();
      } catch (e) {
        // Keep the server-rendered fallback text; surface the cause for debugging.
        if (window.console) console.warn('delivery-estimate: could not compute window', e);
      }
    }

    /** Reads config from data-* attributes and writes the resolved label. */
    update() {
      const minDays = parseInt(this.dataset.minDays, 10);
      const maxDays = parseInt(this.dataset.maxDays, 10);
      const cutoff = parseInt(this.dataset.cutoffHour, 10);
      const saturday = this.dataset.saturday === 'true';
      const prefix = this.dataset.prefix || '';
      if (isNaN(minDays) || isNaN(maxDays)) return;

      const berlin = nowInBerlin();
      const range = computeRange(berlin.date, berlin.hour, { minDays, maxDays, cutoff, saturday });

      const label = this.querySelector('.cart-delivery-estimate__text');
      if (label) label.textContent = (prefix ? prefix + ' ' : '') + range;
    }
  }

  customElements.define('delivery-estimate', DeliveryEstimate);
})();
